import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, toUserMessage } from '@platform/api-client';
import { showAlert, showConfirm } from '@platform/ui';

import { Alert, FormField } from '@/shared/ui';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { ONE_SECOND_MS } from '@/shared/constants/app/time.constant';
import { toAuthenticatedUser } from '@/shared/utils/payload.utils.ts';
import type { AuthenticatedUser } from '@/shared/types/payload.type.ts';
import type { RegisterRequest } from '@/shared/types/auth.type';

import { EMAIL_VERIFY_RESEND_COOLDOWN_SECONDS, useCheckAvailability, useResendVerificationAnonymous, useSignup } from '@/features/auth';
import { BUSINESS_NUMBER_LENGTH, generateNickname, LOGIN_TAB_PARAM } from '@/features/auth';
import { sanitizeLoginId, sanitizeOrgSlug } from '@/features/auth';
import { SignupStepper } from '@/features/auth';
import { PhoneVerifyField } from '@/features/auth';
import {
  signupAgreementsSchema,
  signupAccountSchema,
  signupProfileSchema,
} from '@/features/auth';
import type { AgreementValues } from '@/features/auth/types/agreement-step.type.ts';
import { AgreementsStep } from '@/features/auth/components/AgreementsStep.tsx';
import { signupLegalVersions } from '@/features/auth/utils/consent';
import { UserType } from '@/shared/constants/auth/auth.constant.ts';

interface SignupForm {
  userType: UserType;
  /** 회사코드 (기업 가입 필수). 구성원 로그인 ID 의 `@` 뒤가 되며 확정 후 변경 불가. */
  orgSlug: string;
  agreeTerms: boolean;
  agreePrivacy: boolean;
  agreeMarketing: boolean;
  agreeEvent: boolean;
  email: string;
  loginId: string;
  password: string;
  passwordConfirm: string;
  name: string;
  nickname: string;
  phone: string;
  businessNumber: string;
  companyName: string;
  representativeName: string;
  openDate: string;
}

const STEPS = ['약관 동의', '계정 정보', '프로필'] as const;

type FieldErrors = Partial<Record<keyof SignupForm, string>>;

/**
 * 회원가입 View — 3-step 위저드.
 *
 * - STEP 0: 가입 유형 + 약관 동의
 * - STEP 1: 계정 정보(이메일/아이디/비밀번호)
 * - STEP 2: 프로필(이름/닉네임/연락처) + (기업이면) 사업자 정보
 *
 * 가입 완료 시: 성공 화면으로 전환 + 인증 메일 재발송 가능(60초 쿨다운).
 * 재발송은 비로그인 endpoint 사용 — BE 가 email 로 INACTIVE 사용자만 찾아서 발송.
 */
export function SignupView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(0);
  const emailRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (step === 1) emailRef.current?.focus();
    if (step === 2) nameRef.current?.focus();
  }, [step]);

  const [remainingSeconds, setRemainingSeconds] = useState(0);
  useEffect(() => {
    if (remainingSeconds <= 0) return;
    const timer = setInterval(() => {
      setRemainingSeconds((prev) => prev - 1);
    }, ONE_SECOND_MS);
    return () => clearInterval(timer);
  }, [remainingSeconds]);

  const [form, setForm] = useState<SignupForm>(() => ({
    // 로그인 화면 기업 탭과 같은 `?type=business` 로 들어오면 기업 가입으로 시작한다.
    userType:
      searchParams.get(LOGIN_TAB_PARAM) === 'business'
        ? UserType.BUSINESS
        : UserType.INDIVIDUAL,
    orgSlug: '',
    agreeTerms: false,
    agreePrivacy: false,
    agreeMarketing: false,
    agreeEvent: false,
    email: '',
    loginId: '',
    password: '',
    passwordConfirm: '',
    name: '',
    nickname: generateNickname(),
    phone: '',
    businessNumber: '',
    companyName: '',
    representativeName: '',
    openDate: '',
  }));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [authUser, setAuthUser] = useState<AuthenticatedUser | null>(null);

  const { mutate: register, isPending, error, isSuccess, data, reset } = useSignup();
  const { mutate: resend } = useResendVerificationAnonymous();
  const { mutateAsync: checkAvailability, isPending: isChecking } =
    useCheckAvailability();

  const set = <K extends keyof SignupForm>(key: K, value: SignupForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
    if (error) reset();
  };

  const onInput =
    <K extends keyof SignupForm>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      set(key, e.target.value as SignupForm[K]);

  const isBusiness = form.userType === UserType.BUSINESS;

  // ── 단계별 검증 ────────────────────────────────────────────
  const validateStep = (s: number): boolean => {
    const result =
      s === 0
        ? signupAgreementsSchema.safeParse({
            agreeTerms: form.agreeTerms,
            agreePrivacy: form.agreePrivacy,
            agreeMarketing: form.agreeMarketing,
            agreeEvent: form.agreeEvent,
          })
        : s === 1
          ? signupAccountSchema.safeParse({
              email: form.email,
              loginId: form.loginId,
              orgSlug: form.orgSlug,
              userType: form.userType,
              password: form.password,
              passwordConfirm: form.passwordConfirm,
            })
          : signupProfileSchema.safeParse({
              name: form.name,
              nickname: form.nickname,
              phone: form.phone,
              userType: form.userType,
              businessNumber: form.businessNumber,
              companyName: form.companyName,
              representativeName: form.representativeName,
              openDate: form.openDate,
            });

    if (result.success) {
      // 프로필 단계: 휴대폰 인증 완료(서버 proof)가 가입 필수 조건.
      if (s === 2 && !phoneVerified) {
        setErrors({ phone: '휴대폰 인증을 완료해주세요.' });
        return false;
      }
      setErrors({});
      return true;
    }
    const next: FieldErrors = {};
    for (const issue of result.error.issues) {
      const k = issue.path[0] as keyof SignupForm | undefined;
      if (k && !next[k]) next[k] = issue.message;
    }
    setErrors(next);
    return false;
  };

  /**
   * 계정 스텝(step 1) → 다음 진입 전 이메일/아이디 중복 확인.
   * OTP/프로필 스텝으로 넘어가기 전에 걸러, 인증 완료 후 중복으로 되돌아오는 UX 를 막는다.
   * @returns 진행 가능 여부. 불가 시 해당 필드에 에러를 세팅한다.
   */
  const passesAvailability = async (): Promise<boolean> => {
    try {
      const res = await checkAvailability({
        email: form.email,
        loginId: form.loginId,
        ...(isBusiness ? { orgSlug: form.orgSlug } : {}),
      });
      const next: FieldErrors = {};
      if (!res.email.available) next.email = '이미 가입된 이메일입니다.';
      if (res.loginId.reserved) next.loginId = '사용할 수 없는 아이디입니다.';
      else if (!res.loginId.available)
        next.loginId = '이미 사용 중인 아이디입니다.';
      // 회사코드는 **예약어만** 여기서 걸린다. 사용 중인지는 billing 이 SSOT 라
      // auth 가 답할 수 없다 — 최종 판정은 가입 요청 시 서버가 한다.
      if (res.orgSlug.reserved) next.orgSlug = '사용할 수 없는 회사코드입니다.';

      if (Object.keys(next).length > 0) {
        setErrors(next);
        return false;
      }
      return true;
    } catch (e) {
      setErrors({
        email: toUserMessage(e, '중복 확인 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'),
      });
      return false;
    }
  };

  const goNext = async () => {
    if (!validateStep(step)) return;
    // 계정 스텝에서만 서버 중복 확인 후 진행
    if (step === 1 && !(await passesAvailability())) return;
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
      return;
    }
    submit();
  };

  const goPrev = () => {
    setErrors({});
    setStep((s) => Math.max(0, s - 1));
  };

  const submit = () => {
    const payload: RegisterRequest = {
      email: form.email,
      loginId: form.loginId,
      password: form.password,
      name: form.name,
      nickname: form.nickname || undefined,
      userType: form.userType,
      agreeTerms: form.agreeTerms,
      agreePrivacy: form.agreePrivacy,
      agreeMarketing: form.agreeMarketing,
      agreeEvent: form.agreeEvent,
      // 사용자가 이 화면에서 읽은 약관 판 — 서버가 동의 이력에 그대로 남긴다.
      ...signupLegalVersions(),
      phone: form.phone,
      ...(isBusiness
        ? {
            orgSlug: form.orgSlug,
            businessNumber: form.businessNumber || undefined,
            companyName: form.companyName || undefined,
            representativeName: form.representativeName || undefined,
            openDate: form.openDate ? form.openDate.replace(/-/g, '') : undefined,
          }
        : {}),
    };
    register(payload, {
      onError: (err) => {
        // 국세청 시스템 장애는 사용자 입력 문제가 아님을 별도 알림으로 안내
        if (err instanceof ApiError && err.code === 'NTS_API_UNAVAILABLE') {
          void showAlert({
            title: '국세청 시스템 오류',
            message:
              '국세청 사업자 확인 서비스에 일시적인 문제가 있어 가입을 완료하지 못했습니다.\n잠시 후 다시 시도해주세요.',
            confirmText: '확인',
          });
        }
        // 그 외(검증 실패/중복 등)는 폼 하단 Alert(error)로 노출됨
      },
      onSuccess: (data) => {
        if (data?.user) setAuthUser(toAuthenticatedUser(data.user));
      },
    });
  };

  const agreementValues = useMemo<AgreementValues>(
    () => ({
      agreeTerms: form.agreeTerms,
      agreePrivacy: form.agreePrivacy,
      agreeMarketing: form.agreeMarketing,
      agreeEvent: form.agreeEvent,
    }),
    [form.agreeTerms, form.agreePrivacy, form.agreeMarketing, form.agreeEvent],
  );

  const resendHandler = async () => {
    if (!authUser)
      return showAlert({
        title: '재발송 오류',
        message: '가입 정보가 없어 재발송이 불가능합니다.',
      });
    if (remainingSeconds > 0)
      return showAlert({
        title: '재발송 대기',
        message: `${remainingSeconds}초 후 다시 발송할 수 있습니다.`,
      });

    const ok = await showConfirm({
      title: '인증메일 재발송',
      message: '인증메일을 재발송 하시겠습니까?',
      confirmText: '재발송',
      cancelText: '취소',
    });
    if (!ok) return;

    // BE 도 email_verifications 의 직전 createdAt 으로 60초 쿨다운을 검증한다.
    // 429 AUTH_EMAIL_VERIFY_RESEND_COOLDOWN 응답 시 클라 타이머를 60초로 reset.
    resend(authUser.email, {
      onError: (err) => {
        if (err instanceof ApiError && err.code === 'AUTH_EMAIL_VERIFY_RESEND_COOLDOWN') {
          setRemainingSeconds(EMAIL_VERIFY_RESEND_COOLDOWN_SECONDS);
          void showAlert({
            title: '잠시 후 다시 시도해주세요',
            message: toUserMessage(err, '60초 후 다시 발송할 수 있습니다.'),
          });
          return;
        }
        void showAlert({
          title: '인증메일 재발송 오류',
          message: toUserMessage(
            err,
            '인증메일 재발송이 실패했습니다. 로그인을 하여 재시도 해주세요.',
          ),
          confirmText: '확인',
        });
        return navigate(PATHS.LOGIN);
      },
      onSuccess: (data) => {
        void showAlert({
          title: '재발송 완료',
          message: data.message ?? '인증메일 재발송이 완료되었습니다. 메일함을 확인하세요.',
        });
        setRemainingSeconds(EMAIL_VERIFY_RESEND_COOLDOWN_SECONDS);
      },
    });
  };

  // ── 성공 화면 ──────────────────────────────────────────────
  if (isSuccess) {
    return (
      <div className="mx-auto w-full max-w-md py-12 text-center">
        <div className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-full bg-brand-500/10 text-brand-600">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12.5L10 17.5L19 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h1 className="mb-2 text-2xl font-bold text-adobe-dark">
          가입이 완료되었습니다
        </h1>
        <p className="text-adobe-muted">
          {data?.message ??
            '입력하신 이메일로 인증 링크를 발송했습니다. 메일함을 확인해주세요.'}
        </p>
        <p className="mt-2 text-xs text-adobe-muted">
          메일이 오지 않으면 스팸함을 확인하거나 잠시 후 다시 시도해주세요.
        </p>

        {isBusiness && (
          <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 px-5 py-4 text-left dark:border-amber-500/30 dark:bg-amber-500/10">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              마지막 단계: 기업검증
            </p>
            <p className="mt-1 text-xs leading-relaxed text-amber-800 dark:text-amber-300/90">
              기업 회원은 <b>사업자등록증</b>으로 기업검증을 완료해야 구성원·라이선스·결제
              등 모든 기능을 이용할 수 있습니다. 이메일 인증 후 <b>로그인하면 기업검증
              화면으로 바로 이동</b>하니, 사업자등록증을 준비해 주세요.
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={() => navigate(PATHS.LOGIN, { replace: true })}
          className="mt-6 w-full rounded-lg bg-brand-500 py-2.5 font-bold text-white transition-colors hover:bg-brand-600"
        >
          로그인 페이지로 이동
        </button>

        <p className="mt-5 text-xs text-adobe-muted">
          인증 메일을 확인할 수 없나요?
        </p>

        <p className="mt-1 text-sm text-adobe-muted">
          스팸편지함을 확인하거나{' '}
          <button
            type="button"
            onClick={resendHandler}
            disabled={isPending}
            className="
              underline underline-offset-2
              hover:text-brand-600
              focus-visible:outline-none
              focus-visible:ring-2
              focus-visible:ring-current
              rounded-sm
            "
          >
            {isPending ? '재전송 중...' : '인증 메일 다시 보내기'}
          </button>
        </p>
      </div>
    );
  }

  // ── 위저드 ─────────────────────────────────────────────────
  return (
    <div className="mx-auto w-full max-w-md py-10">
      <header className="mb-7">
        <h1 className="text-2xl font-bold -tracking-[0.02em] text-adobe-dark">
          회원가입
        </h1>
        <p className="mt-1 text-sm text-adobe-muted">
          몇 단계만 거치면 바로 시작할 수 있어요.
        </p>
      </header>

      <SignupStepper steps={STEPS} current={step} />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          goNext();
        }}
        className="space-y-5"
        noValidate
      >
        {/* STEP 0 — 유형 + 약관 */}
        {step === 0 && (
          <div className="space-y-6">
            <fieldset>
              <legend className="mb-2 text-sm font-semibold text-adobe-dark">
                가입 유형
              </legend>
              <div className="grid grid-cols-2 gap-2">
                {(['INDIVIDUAL', 'BUSINESS'] as const).map((t) => {
                  const selected = form.userType === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => set('userType', t)}
                      aria-pressed={selected}
                      className={[
                        'rounded-xl border py-3 text-sm font-semibold transition-all',
                        selected
                          ? 'border-brand-500 bg-brand-500/6 text-brand-700 ring-1 ring-brand-500'
                          : 'border-gray-300 bg-white text-adobe-dark hover:border-brand-300',
                      ].join(' ')}
                    >
                      {t === UserType.INDIVIDUAL ? '개인' : '기업'}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <AgreementsStep
              values={agreementValues}
              onChange={(next) => setForm((prev) => ({ ...prev, ...next }))}
              errors={{
                agreeTerms: errors.agreeTerms,
                agreePrivacy: errors.agreePrivacy,
              }}
            />
          </div>
        )}

        {/* STEP 1 — 계정 */}
        {step === 1 && (
          <div className="space-y-4">
            <FormField
              label="이메일"
              type="email"
              name="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={onInput('email')}
              error={errors.email}
              ref={emailRef}
            />
            {/* 아이디 → @회사코드 순서. 조립되는 로그인 ID 와 같은 순서로 놓는다. */}
            <FormField
              label="아이디"
              type="text"
              name="loginId"
              autoComplete="username"
              required
              value={form.loginId}
              onChange={(e) =>
                set('loginId', sanitizeLoginId(e.target.value))
              }
              error={errors.loginId}
              hint={
                isBusiness
                  ? '영문 소문자/숫자/_ 4~20자. 로그인 ID 는 아이디@회사코드 형태가 됩니다.'
                  : '영문 소문자/숫자/_ 4~20자'
              }
            />
            {isBusiness && (
              <FormField
                label="회사코드"
                type="text"
                name="orgSlug"
                autoComplete="organization"
                placeholder="howcost"
                prefix="@"
                required
                value={form.orgSlug}
                onChange={(e) => set('orgSlug', sanitizeOrgSlug(e.target.value))}
                error={errors.orgSlug}
                hint="영문 소문자/숫자 3~20자. 확정 후에는 변경할 수 없습니다."
              />
            )}
            {isBusiness && !!form.loginId && !!form.orgSlug && (
              <p className="text-xs text-adobe-muted dark:text-neutral-500">
                로그인 ID:{' '}
                <b className="font-mono text-adobe-dark dark:text-neutral-100">
                  {form.loginId}@{form.orgSlug}
                </b>
              </p>
            )}
            <FormField
              label="비밀번호"
              type="password"
              name="password"
              autoComplete="new-password"
              required
              value={form.password}
              onChange={onInput('password')}
              error={errors.password}
              hint="영문과 숫자를 포함해 8자 이상"
            />
            <FormField
              label="비밀번호 확인"
              type="password"
              name="passwordConfirm"
              autoComplete="new-password"
              required
              value={form.passwordConfirm}
              onChange={onInput('passwordConfirm')}
              error={errors.passwordConfirm}
            />
          </div>
        )}

        {/* STEP 2 — 프로필 */}
        {step === 2 && (
          <div className="space-y-4">
            <FormField
              label="이름"
              type="text"
              name="name"
              autoComplete="name"
              required
              value={form.name}
              onChange={onInput('name')}
              error={errors.name}
              ref={nameRef}
            />

            <div>
              <FormField
                label="닉네임"
                type="text"
                name="nickname"
                required
                value={form.nickname}
                onChange={onInput('nickname')}
                error={errors.nickname}
              />
              <button
                type="button"
                onClick={() => set('nickname', generateNickname())}
                className="mt-1.5 text-xs font-semibold text-brand-600 hover:underline"
              >
                ↻ 랜덤 닉네임 다시 생성
              </button>
            </div>

            <div>
              <PhoneVerifyField
                phone={form.phone}
                onPhoneChange={(v) => {
                  set('phone', v);
                  // 번호를 다시 바꾸면 기존 인증 무효화 → 재인증 강제.
                  if (phoneVerified) setPhoneVerified(false);
                }}
                verified={phoneVerified}
                onVerified={() => setPhoneVerified(true)}
              />
              {errors.phone && (
                <p className="mt-1 text-xs text-danger" role="alert">
                  {errors.phone}
                </p>
              )}
            </div>

            {isBusiness && (
              <div className="space-y-4 rounded-xl border border-gray-200 bg-slate-50 p-4">
                <p className="text-[13px] font-semibold text-adobe-dark">
                  기업 정보
                </p>
                <FormField
                  label="회사명"
                  type="text"
                  name="companyName"
                  autoComplete="organization"
                  required
                  value={form.companyName}
                  onChange={onInput('companyName')}
                  error={errors.companyName}
                />
                <FormField
                  label="사업자등록번호"
                  type="text"
                  name="businessNumber"
                  inputMode="numeric"
                  required
                  value={form.businessNumber}
                  onChange={(e) =>
                    set(
                      'businessNumber',
                      e.target.value.replace(/[^0-9]/g, '').slice(0, BUSINESS_NUMBER_LENGTH),
                    )
                  }
                  error={errors.businessNumber}
                  hint="하이픈(-) 없이 숫자만 입력"
                />
                <FormField
                  label="대표자명"
                  type="text"
                  name="representativeName"
                  required
                  value={form.representativeName}
                  onChange={onInput('representativeName')}
                  error={errors.representativeName}
                />
                <FormField
                  label="개업일자"
                  type="date"
                  name="openDate"
                  required
                  value={form.openDate}
                  onChange={onInput('openDate')}
                  error={errors.openDate}
                  hint="국세청 진위확인에 사용됩니다"
                />
                <p className="text-[12px] text-neutral-950/45">
                  입력한 정보가 국세청 사업자 등록정보와 일치해야 가입이 완료됩니다.
                </p>
              </div>
            )}
          </div>
        )}

        {error && <Alert tone="error">{toUserMessage(error)}</Alert>}

        {/* 액션 */}
        <div className="flex gap-2 pt-1">
          {step > 0 && (
            <button
              type="button"
              onClick={goPrev}
              disabled={isPending || isChecking}
              className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-adobe-dark transition-colors hover:bg-black/[0.03] disabled:opacity-50"
            >
              이전
            </button>
          )}
          <button
            type="submit"
            disabled={isPending || isChecking}
            className="flex-1 rounded-lg bg-brand-500 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
          >
            {step < STEPS.length - 1
              ? isChecking
                ? '확인 중…'
                : '다음'
              : isPending
                ? '가입 중…'
                : '가입하기'}
          </button>
        </div>
      </form>

      <p className="mt-6 text-center text-sm text-adobe-muted">
        이미 계정이 있으신가요?{' '}
        <Link
          to={PATHS.LOGIN}
          className="font-semibold text-primary hover:underline"
        >
          로그인
        </Link>
      </p>
    </div>
  );
}
